import type { LngLatLike } from 'mapbox-gl';

export type Coord = [number, number];

type FarthestPointsOptimizedReturn = {
	pointA: GeoJSON.Position;
	pointB: GeoJSON.Position;
	pointC: GeoJSON.Position;
	pointD: GeoJSON.Position;
	latDiff: number;
	lngDiff: number;
};

export const getFarthestPointsOptimized = (coords: GeoJSON.Position[] | undefined): FarthestPointsOptimizedReturn | [] => {
	if (!coords || coords.length < 2) {
		return [];
	}

	// Find the pair with the greatest horizontal (longitude) separation
	let maxLngDiff = 0;
	let maxLngPair = [coords[0], coords[coords.length - 1]];
	// Find the pair with the greatest vertical (latitude) separation
	let maxLatDiff = 0;
	let maxLatPair = [coords[0], coords[coords.length - 1]];

	for (let i = 0; i < coords.length; i++) {
		for (let j = i + 1; j < coords.length; j++) {
			const lngDiff = Math.abs(coords[i][0] - coords[j][0]);
			if (lngDiff > maxLngDiff) {
				maxLngDiff = lngDiff;
				maxLngPair = [coords[i], coords[j]];
			}
			const latDiff = Math.abs(coords[i][1] - coords[j][1]);
			if (latDiff > maxLatDiff) {
				maxLatDiff = latDiff;
				maxLatPair = [coords[i], coords[j]];
			}
		}
	}
	// Return A, B (max lng diff), C, D (max lat diff)
	// and the respective differences

	return {
		pointA: maxLngPair[0],
		pointB: maxLngPair[1],
		pointC: maxLatPair[0],
		pointD: maxLatPair[1],
		latDiff: maxLatDiff,
		lngDiff: maxLngDiff,
	};
};

export const handleMakeRegion = (coordinates: GeoJSON.Position[] | undefined, isFullScreen?: boolean) => {
	const { pointA, pointB, pointC, pointD, latDiff, lngDiff } = getFarthestPointsOptimized(coordinates) as FarthestPointsOptimizedReturn;

	if (!pointA || !pointB || !pointC || !pointD) {
		return;
	}

	// Use A/B for longitude bounds, C/D for latitude bounds
	const minLng = Math.min(pointA[0], pointB[0]);
	const maxLng = Math.max(pointA[0], pointB[0]);
	const minLat = Math.min(pointC[1], pointD[1]);
	const maxLat = Math.max(pointC[1], pointD[1]);

	const coordinate: Coord = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];

	const offsetByZoom = (zoom: number) => {
		const panelPixelOffset = 90;
		return (panelPixelOffset * 360) / (256 * Math.pow(2, zoom));
	};

	const getZoomFromDiff = (lngDiff: number, latDiff: number, paddingW = 1.65, paddingH = 1.5) => {
		const adjustedLng = lngDiff * paddingW;
		const adjustedLat = latDiff * paddingH;

		const zoomLng = Math.log2(360 / adjustedLng);
		const zoomLat = Math.log2(180 / adjustedLat);

		const zoom = Math.min(17, Math.max(1, Math.min(zoomLng, zoomLat)));

		return zoom;
	};

	const zoom = getZoomFromDiff(lngDiff, latDiff);
	const center: LngLatLike = isFullScreen ? [coordinate[0], coordinate[1]] : [coordinate[0], coordinate[1] - offsetByZoom(zoom)];

	return {
		center,
		zoom,
	};
};
